import { Component, OnInit, inject } from '@angular/core'
import { FormGroup, FormControl, Validators } from '@angular/forms'
import { BaseComponent } from '../../../../common/components/base.component'
import { UserStateService } from '../../../../common/store/state/user/user.state.service'
import { finalize } from 'rxjs'
import { CollectionType } from '../../../../common/enums/mfa.enum'
import { MfaService } from '../../../../common/services/mfa/mfa.service'
import { LoginFlowStateService } from '../../../../common/store/state/login-flow/login-flow.state.service'
import { LoginFlow } from '../../../../common/models/Login'

@Component({
  selector: 'app-mfa-selection',
  templateUrl: './mfa-selection.component.html',
  styleUrl: './mfa-selection.component.scss',
  standalone: false
})
export class MfaSelectionComponent extends BaseComponent implements OnInit {

  #userStateService = inject(UserStateService)
  #mfaService = inject(MfaService)
  #loginFlowStateService = inject(LoginFlowStateService)
  selectionForm: FormGroup
  errorMessage: string
  devicesTypes = ['Email']
  subHeaderText = ''
  #loginFlow: LoginFlow
  #mfaSpinnerId = 'mfa-spinner'
  readonly #authenticatorType = 'Authenticator'

  constructor() {
    super()
  }

  ngOnInit(): void {
    this.#loginFlow = this.#loginFlowStateService.getLoginFlow()
    this.subHeaderText = this.#buildSubHeader()

    const user = this.#userStateService.getUser()
    const phone = user?.MobilePhone

    if (phone) {
      this.devicesTypes.unshift('Phone')
    }
    this.devicesTypes.push(this.#authenticatorType)

    const defaultDevice = this.#loginFlow?.TotpEnrolled
      ? this.#authenticatorType
      : (phone ? 'Phone' : 'Email')

    this.selectionForm = new FormGroup({
      selectedDevice: new FormControl(defaultDevice, [Validators.required]),
    })
  }

  deviceDescription(type: string): string {
    if (type === this.#authenticatorType) {
      return this.#loginFlow?.TotpEnrolled
        ? 'Enter a code from your authenticator app'
        : 'Set up an authenticator app'
    }
    return type === 'Phone'
      ? 'Receive verification code via mobile device'
      : 'Receive verification code via email'
  }

  onContinue() {
    if (!this.selectionForm.valid) {
      return
    }

    if (this.selectionForm.value.selectedDevice === this.#authenticatorType) {
      this.router.navigate([this.#loginFlow?.TotpEnrolled ? '/mfa/totp/verify' : '/mfa/totp/setup'])
      return
    }

    this.#validateContact()
  }

  #buildSubHeader(): string {
    const organization = this.#loginFlow?.Organization || 'SARA Plus'
    const timer = this.#loginFlow?.MfaTimerHours
      ? this.#formatTimer(this.#loginFlow.MfaTimerHours)
      : '30 days'
    return `${organization} requires users to validate their device every ${timer}.`
  }

  #formatTimer(hours: number): string {
    if (hours % 24 === 0) {
      const days = hours / 24
      return `${days} ${days === 1 ? 'day' : 'days'}`
    }
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }

  #validateContact() {
    this.errorMessage = ''
    const user = this.#userStateService?.getUser() || null

    if (!user) {
      this.errorMessage = 'User information not found. Please log in again.'
      this.router.navigate(['/login'])
      return
    }

    const selectionType = this.selectionForm.value.selectedDevice.toLowerCase()
    const selectionValue = selectionType === CollectionType.email
      ? user.ContactEmail
      : user.MobilePhone

    if (!selectionValue) {
      this.errorMessage = `No ${selectionType} found for your account. Please contact support.`
      return
    }

    this.spinnerService.setLoading(true, this.#mfaSpinnerId)
    this.subscriptions.add(
      this.#mfaService.validateContact(selectionType, selectionValue)
        .pipe(finalize(() => {
          this.spinnerService.setLoading(false, this.#mfaSpinnerId)
        }))
        .subscribe({
          next: () => {
            this.router.navigate([`/mfa/verification/${selectionType}`])
          }
        }))
  }
}
